import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { 
  UserPlus, 
  Trash2, 
  ShieldCheck, 
  Mail,
  UserX
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/equipe")({
  component: EquipeAdmin,
});

function EquipeAdmin() {
  const { profile: currentAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [newAdminEmail, setNewAdminEmail] = useState("");

  const { data: equipe, isLoading } = useQuery({
    queryKey: ["admin-equipe"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .in("role", ["admin", "super_admin"])
        .order("role", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const promoteMutation = useMutation({
    mutationFn: async (email: string) => {
      // First, check if user exists
      const { data: userProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email)
        .single();

      if (userProfile) {
        // Promote existing user
        const { error } = await supabase
          .from("profiles")
          .update({ role: "admin", status: "aprovado" })
          .eq("id", userProfile.id);
        if (error) throw error;
      } else {
        // In a real world, we'd use Supabase Auth invite. 
        // Here we'll create the profile with status 'aprovado' and role 'admin'
        // and tell the admin they need to invite via Supabase if not using social auth.
        toast.info("Usuário não encontrado. Crie uma conta para ele ou use o convite do Supabase Auth.");
        throw new Error("User not found");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-equipe"] });
      setNewAdminEmail("");
      toast.success("Admin adicionado com sucesso!");
    },
    onError: (err: any) => {
      if (err.message !== "User not found") toast.error("Erro ao adicionar admin.");
    }
  });

  const removeAdminMutation = useMutation({
    mutationFn: async (id: string) => {
      if (id === currentAdmin?.id) throw new Error("Self-removal not allowed");
      const { error } = await supabase
        .from("profiles")
        .update({ role: "usuario" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-equipe"] });
      toast.success("Admin removido da equipe.");
    },
    onError: (err: any) => {
      toast.error(err.message === "Self-removal not allowed" ? "Você não pode remover a si mesmo." : "Erro ao remover admin.");
    }
  });

  if (currentAdmin?.role !== "super_admin") {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold text-red-600">Acesso Negado</h2>
        <p>Apenas Super Admins podem gerenciar a equipe.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 pb-24 md:pb-8 max-w-4xl mx-auto">
      <Card className="p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-primary" />
          Adicionar novo administrador
        </h3>
        <div className="flex gap-2">
          <Input 
            placeholder="E-mail do novo administrador..." 
            type="email"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
          />
          <Button onClick={() => promoteMutation.mutate(newAdminEmail)} disabled={promoteMutation.isPending || !newAdminEmail}>
            Adicionar
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
          <Mail className="w-3 h-3" />
          O usuário deve ter uma conta cadastrada com este e-mail.
        </p>
      </Card>

      <div className="bg-white border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Cargo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-10">Carregando...</TableCell></TableRow>
            ) : equipe?.map((member) => (
              <TableRow key={member.id}>
                <TableCell className="font-medium">{member.nome}</TableCell>
                <TableCell className="text-sm">{member.email}</TableCell>
                <TableCell>
                  <Badge className={cn(
                    "capitalize shadow-none border-none",
                    member.role === "super_admin" ? "bg-primary text-primary-foreground" : "bg-blue-100 text-blue-700"
                  )}>
                    {member.role.replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {member.id !== currentAdmin.id && member.role !== "super_admin" && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500"
                      onClick={() => removeAdminMutation.mutate(member.id)}
                    >
                      <UserX className="w-4 h-4 mr-1" /> Remover
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Card({ children, className }: { children: React.ReactNode, className?: string }) {
  return <div className={cn("bg-white border rounded-xl", className)}>{children}</div>;
}
